import {getTestProps} from '@benchling/js-test-utils';
import {Box, Select, Stack, Text} from '@mantine/core';
import React from 'react';

import SearchInput from 'search/inputs/SearchInput';
import {capitalize} from 'util/NameUtil';

import {EventTriggerConfig, TriggerObjectScope} from '../RoutineTypes';
import RoutineSchemaSelector from './RoutineSchemaSelector';
import {RoutineSchemaOption, RoutineSchemaSource} from './routineSchemaSources';
import SelectableCheckboxCard from './SelectableCheckboxCard';
import {TargetScopeCapabilities, TriggerScope} from './types';

interface TargetScopeSectionProps {
  capabilities: TargetScopeCapabilities;
  commonNounLabel: string;
  config: Partial<EventTriggerConfig>;
  getCardClassName: (selected: boolean, disabled: boolean) => string;
  pluralObjectTypeLabel: string;
  onChange: (updates: Partial<EventTriggerConfig>) => void;
  onScopeChange: (nextScope: TriggerScope) => void;
  scope: TriggerScope | undefined;
  searchInputContainerClassName: string;
  sectionClassName: string;
  selectDropdownClassName: string;
  selectedObjectSearchType: string;
  selectedObjectType: string;
  selectedSchemaSource: RoutineSchemaSource | null;
  schemaOptions: Array<RoutineSchemaOption>;
  schemasLoading: boolean;
  schemasError: boolean;
}

export default function TargetScopeSection({
  capabilities,
  commonNounLabel,
  config,
  getCardClassName,
  pluralObjectTypeLabel,
  onChange,
  onScopeChange,
  scope,
  searchInputContainerClassName,
  sectionClassName,
  selectDropdownClassName,
  selectedObjectSearchType,
  selectedObjectType,
  selectedSchemaSource,
  schemaOptions,
  schemasLoading,
  schemasError,
}: TargetScopeSectionProps): JSX.Element {
  const selectedObjectScopes = config.object_scopes ?? [];
  const toggleObjectScope = (objectScope: TriggerObjectScope): void => {
    const nextObjectScopes = selectedObjectScopes.includes(objectScope)
      ? selectedObjectScopes.filter((existing) => existing !== objectScope)
      : [...selectedObjectScopes, objectScope];
    onChange({object_scopes: nextObjectScopes});
  };

  // TODO(BNCH-163807): Consider adding tooltip to disabled options.
  const scopeOptions = [
    {value: 'all', label: `All ${pluralObjectTypeLabel}`, disabled: false},
    {value: 'schema', label: `A type of ${commonNounLabel}`, disabled: capabilities.schemaScopeDisabled},
    {
      value: 'instance',
      label: `A specific ${commonNounLabel}`,
      disabled: capabilities.instanceScopeDisabled,
    },
  ];

  // The "Restrict to…" block only appears once the scope selection is fully specified: "all" needs
  // nothing more, but "a type of…"/"a specific…" require the nested schema/instance to be chosen.
  const scopeSelectionComplete =
    scope === 'all' ||
    (scope === 'schema' && (config.schema_id ?? '') !== '') ||
    (scope === 'instance' && (config.object_ids ?? []).length > 0);

  return (
    <Stack spacing={4} className={sectionClassName}>
      <Box {...getTestProps({component: 'EventTriggerConfigForm', element: 'scopeSelect'})}>
        <Select
          aria-label="Target scope"
          placeholder="Select scope"
          value={scope ?? ''}
          onChange={(value) => {
            if (value != null) {
              onScopeChange(value as TriggerScope);
            }
          }}
          data={scopeOptions}
          classNames={{dropdown: selectDropdownClassName}}
          size="md"
        />
      </Box>

      {scope === 'schema' && selectedSchemaSource != null && (
        <Box className={searchInputContainerClassName}>
          <RoutineSchemaSelector
            selectedSchemaId={config.schema_id ?? null}
            onChange={onChange}
            schemas={schemaOptions}
            loading={schemasLoading}
            error={schemasError}
            dropdownClassName={selectDropdownClassName}
          />
        </Box>
      )}

      {scope === 'instance' && selectedObjectType !== '' && (
        <Box className={searchInputContainerClassName}>
          <SearchInput
            types={[selectedObjectSearchType]}
            selected={config.object_ids ?? []}
            onSelect={(id) => {
              if (id != null && !(config.object_ids ?? []).includes(id)) {
                onChange({object_ids: [...(config.object_ids ?? []), id]});
              }
            }}
            onRemove={(id) => {
              onChange({
                object_ids: (config.object_ids ?? []).filter((existingId) => existingId !== id),
              });
            }}
            hideSelectedResults={true}
            isMulti={true}
            placeholder={`Search for ${commonNounLabel}...`}
            dropLeft={true}
          />
        </Box>
      )}

      {scopeSelectionComplete && (
        <Box ml="lg">
          <Stack spacing={4} className={sectionClassName}>
            <Text size="md" c="dimmed">
              Restrict to…
            </Text>
            <Stack role="group" aria-label="Restrict to" spacing="sm">
              <SelectableCheckboxCard
                label={`All ${pluralObjectTypeLabel} I can view`}
                checked={selectedObjectScopes.includes('viewable')}
                onToggle={() => {
                  toggleObjectScope('viewable');
                }}
                cardClassName={getCardClassName(selectedObjectScopes.includes('viewable'), false)}
              />
              <SelectableCheckboxCard
                label={`${capitalize(pluralObjectTypeLabel)} I've created`}
                checked={selectedObjectScopes.includes('authored')}
                onToggle={() => {
                  toggleObjectScope('authored');
                }}
                cardClassName={getCardClassName(selectedObjectScopes.includes('authored'), false)}
              />
            </Stack>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
